import {
  afterNextRender,
  inject,
  InjectionToken,
  Injector,
  Service,
  signal,
  type AfterRenderRef,
  type Signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { CurrentAccountService } from '@aurora/modules/authentication';
import { TranslocoService } from '@jsverse/transloco';
import { driver, type Config, type Driver } from 'driver.js';
import { take, type Subscription } from 'rxjs';
import type { Tour, TourScreen } from './manual-tour';

/** Builds the driver.js instance; a token so specs can inspect the config it receives. */
export const TOUR_DRIVER_FACTORY = new InjectionToken<
  (config: Config) => Pick<Driver, 'drive' | 'destroy'>
>('TOUR_DRIVER_FACTORY', { factory: () => driver });

/**
 * Runs already-loaded `Tour`s with driver.js. Source-agnostic: whoever loads a screen's tours
 * hands them over (`startWhenRendered` on the first visit, `register` so the help action can
 * replay each one). What the user has seen lives in the account
 * (`CurrentAccountService.metaList('tours')`), so it follows the user across devices.
 *
 * A general tour (`screen` set) targets roles, not full `data-tour` values, and applies to the
 * screen on display only when the screen is of its kind: a form shows `save`; a list shows `table` and
 * no `save` (a generated list embedded in a form page keeps its `table` mark).
 */
@Service()
export class TourRunnerService {
  private readonly account = inject(CurrentAccountService);
  private readonly createDriver = inject(TOUR_DRIVER_FACTORY);
  private readonly _currentTours = signal<Tour[]>([]);
  private readonly seenTours = this.account.metaList(TOURS_META_KEY);
  private readonly injector = inject(Injector);
  private readonly transloco = inject(TranslocoService);
  private readonly router = inject(Router);
  private pendingScreen: MutationObserver | null = null;
  private pendingScope: Subscription | null = null;
  private pendingRender: AfterRenderRef | null = null;
  /** URL the pending wait was requested on (`replayWhenRendered`); `null` for a first-visit wait. */
  private requestedOn: string | null = null;
  /** Tours the screen on display registered; re-listed once a requested tour finds its screen rendered. */
  private registered: Tour[] = [];
  private readonly _isTourRunning = signal(false);
  private activeDriver: Pick<Driver, 'destroy'> | null = null;

  /** Whether a tour is on screen: set at start, cleared when driver.js destroys it. */
  readonly isTourRunning: Signal<boolean> = this._isTourRunning.asReadonly();

  /** Tours that apply to the screen on display, general ones first. Offered by the help action. */
  readonly currentTours: Signal<Tour[]> = this._currentTours.asReadonly();

  /** A screen's loader declares its candidate tours on enter and clears them (`[]`) on leave. */
  register(tours: Tour[]): void {
    // The screen's own register runs after the navigation a requested tour waited for, so only
    // leaving that screen cancels the request.
    if (this.requestedOn !== this.router.url) this.stopWaiting();
    // Leaving the screen ends its tour: otherwise the overlay stays over the new screen and
    // `isTourRunning` blocks that screen's own tour. Counts as closed (recorded once, like a close).
    this.activeDriver?.destroy();
    this.registered = tours;
    this._currentTours.set(applicableTours(tours));
  }

  /**
   * First-visit trigger for the tours of the screen on display: once the screen has its targets
   * in the DOM, lists the tours that apply (`currentTours`) and starts the unseen ones
   * (`startIfUnseen`). A screen's content can arrive after the render that followed the navigation
   * (e.g. behind `*transloco`, which waits for the scope file), so when no target is there yet
   * this observes the DOM until one appears. `register` cancels the wait; a pending
   * `replayWhenRendered` request wins over it.
   */
  startWhenRendered(tours: Tour[]): void {
    if (this.requestedOn !== null) return;
    this.stopWaiting();
    if (tours.length === 0) return;
    this.whenRendered(
      () => this.screenRendered(tours),
      () => this.begin(tours),
    );
  }

  /**
   * Like `startWhenRendered` but for a tour somebody asked for (the assistant's guided action):
   * replays it whatever the user has seen once a target is on screen, and tells `onEnded`
   * whether the user finished it (`true`) or closed it. `register` cancels it only once the
   * user leaves the screen it was requested on.
   */
  replayWhenRendered(tour: Tour, onEnded?: (completed: boolean) => void): void {
    this.stopWaiting();
    this.requestedOn = this.router.url;
    this.whenRendered(
      () => hasAnyTarget(tour),
      () => {
        this.requestedOn = null;
        // The pending request kept the first-visit `begin` from listing the tours of this screen.
        this._currentTours.set(applicableTours(this.registered));
        // An explicit request wins over a first-visit tour on screen; closing it records it as seen.
        this.activeDriver?.destroy();
        this.replay(tour, onEnded);
      },
    );
  }

  private whenRendered(isReady: () => boolean, start: () => void): void {
    // The popover buttons are translated, so the `manual` scope must be loaded before the driver is.
    this.pendingScope = this.transloco
      .selectTranslation('manual')
      .pipe(take(1))
      .subscribe(() => {
        // Kept so `stopWaiting` can cancel a render callback that has not run yet.
        this.pendingRender = afterNextRender(
          () => {
            this.pendingRender = null;
            if (isReady()) {
              start();
              return;
            }
            this.pendingScreen = new MutationObserver(() => {
              if (!isReady()) return;
              this.stopWaiting();
              start();
            });
            this.pendingScreen.observe(document.body, { childList: true, subtree: true });
          },
          { injector: this.injector },
        );
      });
  }

  /**
   * Starts, as one driver run, the unseen tours among those that apply to the screen: general
   * steps first, then the own ones. Closing the run records every tour of it as seen. `false`
   * when nothing started.
   */
  startIfUnseen(tours: Tour[]): boolean {
    const unseen = applicableTours(tours).filter((tour) => this.isUnseen(tour));
    return this.run(unseen, true);
  }

  /** Starts one tour from its first step whatever the user has seen; records nothing. */
  replay(tour: Tour, onEnded?: (completed: boolean) => void): boolean {
    return this.run([tour], false, onEnded);
  }

  private begin(tours: Tour[]): void {
    this._currentTours.set(applicableTours(tours));
    this.startIfUnseen(tours);
  }

  private screenRendered(tours: Tour[]): boolean {
    return applicableTours(tours).some(hasAnyTarget);
  }

  private isUnseen(tour: Tour): boolean {
    // No account (not loaded yet, or anonymous): the seen list is unknown, so never guess.
    return this.account.accountId() !== null && !this.seen().includes(tour.id);
  }

  private stopWaiting(): void {
    this.requestedOn = null;
    this.pendingScope?.unsubscribe();
    this.pendingScope = null;
    this.pendingRender?.destroy();
    this.pendingRender = null;
    this.pendingScreen?.disconnect();
    this.pendingScreen = null;
  }

  private seen(): readonly string[] {
    return this.seenTours().filter((id): id is string => typeof id === 'string');
  }

  private run(tours: Tour[], record: boolean, onEnded?: (completed: boolean) => void): boolean {
    if (this._isTourRunning()) return false;
    // A step whose target is not on screen (e.g. hidden by a permission) is skipped.
    const steps = tours
      .flatMap((tour) =>
        tour.steps.map((step) => ({ step, selector: selectorOf(tour, step.target) })),
      )
      .filter(({ selector }) => document.querySelector(selector) !== null)
      .map(({ step, selector }) => ({
        element: selector,
        popover: { title: step.title, description: step.text },
      }));
    if (steps.length === 0) return false;

    this._isTourRunning.set(true);
    let recorded = false;
    const activeDriver = this.createDriver({
      steps,
      prevBtnText: this.transloco.translate('manual.tour.Previous'),
      nextBtnText: this.transloco.translate('manual.tour.Next'),
      doneBtnText: this.transloco.translate('manual.tour.Done'),
      popoverClass: 'au-tour-popover',
      // driver.js calls this on finish AND on close.
      onDestroyed: (_element, _step, { reason }) => {
        this._isTourRunning.set(false);
        this.activeDriver = null;
        onEnded?.(reason === 'done');
        if (!record || recorded) return;
        recorded = true;
        // One write: two concurrent ones would each replace the list with the server's answer.
        this.account.addMetaItems(
          TOURS_META_KEY,
          tours.map((tour) => tour.id),
        );
      },
    });
    this.activeDriver = activeDriver;
    activeDriver.drive();
    return true;
  }
}

/** Owner-writable `meta` list that holds the ids of the tours the user already saw. */
const TOURS_META_KEY = 'tours';

const roleSelector = (role: string): string => `[data-tour$=".${role}"]`;

const selectorOf = (tour: Tour, target: string): string =>
  tour.screen === null ? `[data-tour="${target}"]` : roleSelector(target);

const isOnScreen = (role: string): boolean => document.querySelector(roleSelector(role)) !== null;

/**
 * Whether the screen on display is of each kind. A form shows `save`. A list shows `table` and no
 * `save`: a list embedded in a detail page keeps its `table` mark inside a page that also has
 * `save`, and that page is a form.
 */
const SCREEN_IS: Record<TourScreen, () => boolean> = {
  list: () => isOnScreen('table') && !isOnScreen('save'),
  form: () => isOnScreen('save'),
};

/** The own tour always applies; a general one when the screen on display is of its kind. */
const applicableTours = (tours: Tour[]): Tour[] =>
  tours
    .filter((tour) => tour.screen === null || SCREEN_IS[tour.screen]())
    .sort((a, b) => Number(a.screen === null) - Number(b.screen === null));

const hasAnyTarget = (tour: Tour): boolean =>
  tour.steps.some((step) => document.querySelector(selectorOf(tour, step.target)) !== null);
